import { PortfolioSnapshot, Transaction } from '../types';

/**
 * Per-wallet coin balances over TIME, and what that allows you to remove.
 *
 * The guards this replaces asked "what is in the wallet right now". That is the
 * wrong question twice over:
 *
 *   1. A back-dated sell was checked against today's balance, so you could sell
 *      coins dated before you ever bought them.
 *   2. A balance that is fine today says nothing about whether removing coins at
 *      an earlier date leaves a LATER sell unfunded. Hold 1 BTC, sell it on the
 *      15th, then add a 0.5 sell on the 10th: every as-at-now check passes, and
 *      the 15th is now short.
 *
 * So everything here works on the wallet's whole timeline rather than its
 * endpoint. One pure module, no React, so the Add form, the Edit modal, the
 * delete confirm and the warning banner cannot disagree about what is funded.
 */

/** Float tolerance, matching the guards this replaces. */
export const WALLET_EPS = 1e-9;

/** BUY/SELL rows with no wallet are grouped here, as elsewhere in the app. */
export const UNSPECIFIED_WALLET = 'Unspecified';

/** ISO dates compare correctly as strings once trimmed to the day. */
function dayOf(iso: string): string {
  return (iso || '').slice(0, 10);
}

interface Delta {
  day: string;
  /** Preserves input order within a day so the walk is deterministic. */
  seq: number;
  amount: number;
  tx: Transaction;
}

/**
 * Signed movements for one (coin, wallet) pair, oldest first.
 *
 * TRANSFER contributes to two wallets: out of `fromWallet`, into `toWallet`.
 * Only BUY and SELL fall back to "Unspecified", which is how the rest of the app
 * attributes wallet-less rows.
 */
function deltasFor(
  txs: Transaction[],
  coinId: string,
  wallet: string,
  excludeTxId?: string,
): Delta[] {
  const out: Delta[] = [];
  txs.forEach((tx, i) => {
    // Guarded on excludeTxId being set: an id-less row must not be dropped
    // just because both sides happen to be undefined.
    if (excludeTxId && tx.id === excludeTxId) return;
    if (tx.coinId !== coinId) return;
    if (tx.type === 'TRANSFER') {
      if (tx.fromWallet === wallet) out.push({ day: dayOf(tx.date), seq: i, amount: -tx.amount, tx });
      if (tx.toWallet === wallet) out.push({ day: dayOf(tx.date), seq: i, amount: tx.amount, tx });
      return;
    }
    const w = tx.wallet?.trim() || UNSPECIFIED_WALLET;
    if (w !== wallet) return;
    out.push({ day: dayOf(tx.date), seq: i, amount: tx.type === 'BUY' ? tx.amount : -tx.amount, tx });
  });
  return out.sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : a.seq - b.seq));
}

/**
 * The largest amount that can be removed from `wallet` on `date` without any
 * point in that wallet's timeline going negative.
 *
 * Removing X on `date` lowers the running balance by X at that date AND at every
 * point after it, so the answer is the MINIMUM running balance from `date`
 * onwards, not the balance on the day itself. Same-day rows already recorded
 * count against you: asking what you can sell today, having already sold today,
 * gives the remainder.
 *
 * `excludeTxId` leaves out the row being edited, so editing a sell is measured
 * against a timeline that does not already contain it.
 *
 * Returns 0 rather than a negative number when the wallet is already oversold.
 */
export function maxSellableAt(
  txs: Transaction[],
  coinId: string,
  wallet: string,
  date: string,
  excludeTxId?: string,
): number {
  if (!coinId || !wallet || !date) return 0;
  const day = dayOf(date);
  const deltas = deltasFor(txs, coinId, wallet, excludeTxId);

  let running = 0;
  // Everything up to and including the chosen day.
  let i = 0;
  for (; i < deltas.length && deltas[i].day <= day; i++) running += deltas[i].amount;

  let floor = running;
  // Then every later point, because removing coins now also removes them later.
  for (; i < deltas.length; i++) {
    running += deltas[i].amount;
    if (running < floor) floor = running;
  }

  return floor > WALLET_EPS ? floor : 0;
}

/** Every wallet that has ever touched this coin, in first-seen order. */
export function walletsForCoin(txs: Transaction[], coinId: string): string[] {
  const seen = new Set<string>();
  for (const tx of txs) {
    if (tx.coinId !== coinId) continue;
    if (tx.type === 'TRANSFER') {
      if (tx.fromWallet) seen.add(tx.fromWallet);
      if (tx.toWallet) seen.add(tx.toWallet);
      continue;
    }
    seen.add(tx.wallet?.trim() || UNSPECIFIED_WALLET);
  }
  return Array.from(seen);
}

/** A sell or transfer-out that the wallet's timeline cannot fund. */
export interface UnfundedDisposal {
  txId: string;
  coinId: string;
  symbol: string;
  name: string;
  /** The transaction's own date, unformatted. */
  date: string;
  wallet: string;
  type: 'SELL' | 'TRANSFER';
  amount: number;
  /** How far below zero this row drove the wallet. */
  shortfall: number;
}

/**
 * Every sell or transfer-out that leaves its wallet below zero, across all
 * coins, all wallets and all dates.
 *
 * Walks each (coin, wallet) timeline in date order and reports the row that
 * crossed the line, not the whole wallet, so the UI can name a transaction.
 * A wallet that goes negative and is later topped up still reports: the sell was
 * unfunded when it happened, which is exactly what the CGT engine objects to.
 */
export function findUnfundedDisposals(txs: Transaction[]): UnfundedDisposal[] {
  const pairs = new Map<string, { coinId: string; wallet: string }>();
  for (const tx of txs) {
    if (tx.type === 'TRANSFER') {
      if (tx.fromWallet) pairs.set(`${tx.coinId} ${tx.fromWallet}`, { coinId: tx.coinId, wallet: tx.fromWallet });
      if (tx.toWallet) pairs.set(`${tx.coinId} ${tx.toWallet}`, { coinId: tx.coinId, wallet: tx.toWallet });
      continue;
    }
    const w = tx.wallet?.trim() || UNSPECIFIED_WALLET;
    pairs.set(`${tx.coinId} ${w}`, { coinId: tx.coinId, wallet: w });
  }

  const found: UnfundedDisposal[] = [];
  for (const { coinId, wallet } of Array.from(pairs.values())) {
    let running = 0;
    for (const d of deltasFor(txs, coinId, wallet)) {
      running += d.amount;
      if (running < -WALLET_EPS && d.amount < 0) {
        found.push({
          txId: d.tx.id,
          coinId: d.tx.coinId,
          symbol: d.tx.symbol,
          name: d.tx.name,
          date: d.tx.date,
          wallet,
          type: d.tx.type === 'TRANSFER' ? 'TRANSFER' : 'SELL',
          amount: d.tx.amount,
          shortfall: -running,
        });
        // Keep walking from zero: one bad row should not cascade into reporting
        // every later sell in the same wallet as broken too.
        running = 0;
      }
    }
  }
  return found.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/**
 * What a proposed change would BREAK that was not already broken.
 *
 * Edits and deletes are judged against the existing state rather than against
 * perfection: data that is already oversold must still be editable, or the user
 * cannot clean it up. Only newly unfunded rows are reported.
 */
export function newlyUnfunded(before: Transaction[], after: Transaction[]): UnfundedDisposal[] {
  const wasBroken = new Set(findUnfundedDisposals(before).map((u) => u.txId));
  return findUnfundedDisposals(after).filter((u) => !wasBroken.has(u.txId));
}

/**
 * What deleting this row would break, as one sentence, or null if nothing.
 *
 * Deletion is never blocked: bad data has to be removable or it cannot be
 * cleaned up. The confirm step just stops it being a surprise.
 */
export function describeDeletionImpact(txs: Transaction[], txId: string): string | null {
  const broken = newlyUnfunded(txs, txs.filter((t) => t.id !== txId));
  if (!broken.length) return null;
  // One deletion can only disturb one coin's timelines, so every broken row
  // here shares a symbol. No plural-asset case to handle.
  const what = broken[0].symbol;
  const allTransfers = broken.every((b) => b.type === 'TRANSFER');
  const noun = allTransfers
    ? (broken.length === 1 ? 'transfer' : 'transfers')
    : (broken.length === 1 ? 'sell' : 'sells');
  return `Deleting this leaves ${broken.length} later ${noun} without enough ${what}.`;
}

/** "12 Oct 2026" — Australian order, matching the app's audience. */
export function formatDateAU(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Coin amounts at the precision used elsewhere: up to 6 decimals, no padding. */
export function formatCoinAmount(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 6 });
}

/**
 * One wallet's balance of each coin at the END of each day in `days`.
 *
 * Same per-wallet rules as everything else in this module (and the Holdings
 * wallet filter): a TRANSFER into the wallet adds, out of it removes, and BUY /
 * SELL rows with no wallet belong to `UNSPECIFIED_WALLET`. Movements dated
 * before the first day are already applied on it.
 *
 * `days` must be ascending ISO dates. A balance driven below zero by an unfunded
 * disposal is shown as zero: a chart of what a wallet held cannot hold less than
 * nothing, and the oversell is reported elsewhere (findUnfundedDisposals).
 */
export function walletBalancesByDay(
  txs: Transaction[],
  wallet: string,
  days: readonly string[],
): Map<string, number[]> {
  const coins = new Set<string>();
  for (const tx of txs) {
    const touches = tx.type === 'TRANSFER'
      ? tx.fromWallet === wallet || tx.toWallet === wallet
      : (tx.wallet?.trim() || UNSPECIFIED_WALLET) === wallet;
    if (touches) coins.add(tx.coinId);
  }

  const out = new Map<string, number[]>();
  coins.forEach((coinId) => {
    const deltas = deltasFor(txs, coinId, wallet);
    let i = 0;
    let balance = 0;
    out.set(coinId, days.map((day) => {
      while (i < deltas.length && deltas[i].day <= day) balance += deltas[i++].amount;
      return balance > WALLET_EPS ? balance : 0;
    }));
  });
  return out;
}

/**
 * A wallet's USD value on each day: its end-of-day balances times
 * `usdPriceOn(coinId, dayIndex)`, the same price lookup the whole-portfolio
 * series uses, so the two lines are priced identically.
 *
 * Starts on the first day the wallet held anything (leading empty days are
 * dropped, so the line begins where the wallet does) and is empty if it held
 * nothing at all in the window. Trailing zeros are kept: a wallet that was
 * emptied part-way through really was worth nothing afterwards.
 */
export function walletValueSeries(
  txs: Transaction[],
  wallet: string,
  days: readonly string[],
  usdPriceOn: (coinId: string, dayIndex: number) => number,
): PortfolioSnapshot[] {
  const balances = walletBalancesByDay(txs, wallet, days);
  const series = days.map((date, d) => {
    let value = 0;
    balances.forEach((bal, coinId) => {
      if (bal[d] > 0) value += bal[d] * usdPriceOn(coinId, d);
    });
    return { date, value };
  });

  const firstHeld = days.findIndex((_, d) => {
    let held = false;
    balances.forEach((bal) => { if (bal[d] > 0) held = true; });
    return held;
  });
  return firstHeld === -1 ? [] : series.slice(firstHeld);
}
